import { useCallback, useEffect, useRef, useState } from 'react';
import { useTrackerConnection } from '../../tracker-connection';
import { useSearchParams } from 'react-router-dom';
import {
  OAUTH_PATHS,
  accountSchema,
  createdTokenSchema,
  oauthConnectionListSchema,
  trackerConnectionSchema,
  type Account,
  type OauthConnection,
  type TrackerConnection,
} from '@forecastify/shared';
import { Toaster, type Toast } from '@forecastify/ui';
import { parseList } from '../permissions-hook';
import type { MessageKey } from '../../i18n/catalogue';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { useFirstLoad } from '../use-first-load';
import { createPasskey, isCancellation } from '../../webauthn';
import { beginLink, beginPkce } from '../auth/pkce';
import type { GoogleConnection } from './integrations-section';
import { ACCOUNT_SECTIONS, AccountScreen, type AccountSectionId } from './account-screen';
import { takeConnectReturn, type ConnectReturnRead } from '../integrations/connect-return';
import {
  memberSkillSchema,
  skillLevelSchema,
  skillSchema,
  type MemberSkill,
  type Skill,
  type SkillLevel,
} from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The account screen, with its data. The screen itself stays dumb.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **A separate component from `AccountScreen`, on purpose.** The screen takes props and renders;
 * this one fetches, parses, mutates and re-reads. Keeping them apart is what let the screen be
 * tested against the real wire shape with no network at all — and it means a change here cannot
 * change the markup.
 *
 * ⚠️ **The response is PARSED with the shared schema, not cast.** `as Account` on a `fetch` result
 * is a lie the compiler believes: the server could answer anything and the first symptom would be
 * `undefined` deep inside a render. `safeParse` turns a contract mismatch into one legible failure
 * at the boundary.
 */
export function AccountRoute() {
  const t = useT();
  /**
   * ⚠️ **The ADDRESS is the section, and it is CHECKED rather than cast.** The sidebar's rows link to
   * `?section=` (settings mode, 13 Oct 2026), so this follows the URL rather than reading it once;
   * an unknown value opens Profile, which is the harmless answer.
   */
  const [params, setParams] = useSearchParams();
  const asked = params.get('section');
  const section: AccountSectionId = (ACCOUNT_SECTIONS as readonly string[]).includes(asked ?? '')
    ? (asked as AccountSectionId)
    : 'profile';
  const [connections, setConnections] = useState<readonly TrackerConnection[] | null>(null);
  /* 21 Oct 2026 · the chrome's missing-Jira marks follow this screen's list. */
  const trackerConnection = useTrackerConnection();
  /* C54 · the caller's own skills, the scale, and the vocabulary to pick from. */
  const [mySkills, setMySkills] = useState<readonly MemberSkill[]>([]);
  const [skillLevels, setSkillLevels] = useState<readonly SkillLevel[]>([]);
  const [skillVocabulary, setSkillVocabulary] = useState<readonly Skill[]>([]);
  const [skillError, setSkillError] = useState<string | null>(null);
  /** ⚠️ C52 · the applications this person approved. `null` is *"not read yet"*, and the card
   * hides itself rather than explaining OAuth to somebody who has never used it. */
  const [applications, setApplications] = useState<readonly OauthConnection[] | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const session = useSession();
  const [account, setAccount] = useState<Account | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  /* Lives only between enrolling a TOTP factor and verifying it. */
  const enrolledFactor = useRef<string | null>(null);
  /**
   * ⚠️⚠️ **THE 2FA STATE IS READ FROM THE SERVER NOW, WHERE THE CARD USED TO GUESS — item 11.**
   * `security-section.tsx` has taken a `twoFactorEnabled` prop since C6 and no route ever supplied
   * one, so somebody with two-factor switched on was offered *Enable* after every reload.
   */
  const [twoFactor, setTwoFactor] = useState<{
    enabled: boolean;
    factorId: string | null;
    recoveryCodesLeft: number;
  } | null>(null);
  const [passkeys, setPasskeys] = useState<
    readonly { id: string; name: string; createdAt: string; lastUsedAt: string | null }[]
  >([]);
  const [sessions, setSessions] = useState<
    readonly {
      sessionId: string;
      userAgent: string | null;
      ip: string | null;
      lastSeenAt: string;
      current: boolean;
    }[]
  >([]);

  /**
   * ⚠️ **Its own callback, because four handlers re-read it after they change it** — enabling,
   * disabling, verifying and regenerating all move the count or the switch, and a card showing
   * yesterday's answer is the defect item 11 is fixing rather than a smaller version of it.
   */
  const readTwoFactor = useCallback(async (): Promise<void> => {
    const response = await session.request('/auth/totp').catch(() => null);
    if (response?.ok !== true) return;
    const body = (await response.json()) as {
      enabled: boolean;
      factor_id: string | null;
      recovery_codes_left: number;
    };
    setTwoFactor({
      enabled: body.enabled,
      factorId: body.factor_id,
      recoveryCodesLeft: body.recovery_codes_left,
    });
  }, [session]);

  const reload = useCallback(async (): Promise<void> => {
    /**
     * ⚠️ **No workspace means no request.** Every guarded route needs `x-workspace-id`, and without
     * one the guard answers 404 — which the first version turned into a permanent spinner. This is a
     * real state (an account nobody has invited anywhere) and it gets said out loud.
     */
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session.request('/account').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = accountSchema.safeParse(await response.json());
    /* ⚠️ A parse failure is a FAILURE, not silence. The first version kept the previous state and
     * showed nothing, so a contract drift looked exactly like a slow network. */
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setAccount(parsed.data);

    /**
     * ⚠️ **Passkeys and devices are separate reads, deliberately.** `GET /account` is the stored
     * account; these two are about *credentials and browsers*, live on `/auth`, and a person who has
     * never opened the Security tab should not pay for them on every load of Profile. They are
     * fetched alongside rather than blocking — a failure here leaves the rest of the screen usable.
     */
    const keys = await session.request('/auth/passkeys').catch(() => null);
    if (keys?.ok === true) {
      const body = (await keys.json()) as { data?: typeof passkeys };
      setPasskeys(body.data ?? []);
    }

    const devices = await session.request('/auth/sessions').catch(() => null);
    if (devices?.ok === true) {
      const body = (await devices.json()) as {
        data?: {
          sessionId: string;
          userAgent: string | null;
          ip: string | null;
          lastSeenAt: string;
          revokedAt: string | null;
          current: boolean;
        }[];
      };
      /* ⚠️ `current` comes from the SERVER, which is the only side that knows which GoTrue session
       * the caller's token belongs to. Defaulting it here would mark every row as somebody else's
       * and offer to sign out the browser you are using. */
      setSessions((body.data ?? []).filter((row) => row.revokedAt === null));
    }

    await readTwoFactor();
  }, [session, readTwoFactor]);

  const loading = useFirstLoad(reload);

  /** What this person could connect. Its own read — nothing else on this screen needs it. */
  const loadConnections = useCallback(async (): Promise<void> => {
    const response = await session.request('/integrations/connections').catch(() => null);
    /* ⚠️ A refusal is `[]`, not a screen failure: a member without `worklog.create` has no
       connections to make, and the card simply is not drawn. */
    if (response?.ok !== true) {
      setConnections([]);
      return;
    }
    {
      const list = parseList(trackerConnectionSchema, await response.json()) ?? [];
      setConnections(list);
      trackerConnection.update(list);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, [session.workspaceId]);

  useEffect(() => {
    void loadConnections();
  }, [loadConnections]);

  /**
   * ⚠️ **Its own read, like the passkeys and the devices**, and for the same reason: a person who
   * has never connected an application should not pay for this on every load of Profile.
   *
   * ⚠️ **Account-level, not workspace-level.** A connection follows the PERSON — the API lists them
   * across every workspace — so this does not re-run when the switcher moves, and the row says
   * which workspace each grant applies to.
   */
  const loadApplications = useCallback(async (): Promise<void> => {
    const response = await session.request(OAUTH_PATHS.connections).catch(() => null);
    /* ⚠️ `[]` rather than a screen failure: this card is one of several on a tab, and a read that
       did not land must not take the rest of the account with it. */
    if (response?.ok !== true) {
      setApplications([]);
      return;
    }
    const parsed = oauthConnectionListSchema.safeParse(await response.json());
    setApplications(parsed.success ? parsed.data.data : []);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, []);

  useEffect(() => {
    void loadApplications();
  }, [loadApplications]);

  /**
   * 22 Oct 2026 · the Google account connected to this one. Account-level like the applications,
   * and non-fatal the same way: a read that did not land hides the card rather than the tab.
   */
  const [google, setGoogle] = useState<GoogleConnection | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const loadGoogle = useCallback(async (): Promise<void> => {
    const response = await session.request('/auth/google').catch(() => null);
    if (response?.ok !== true) {
      setGoogle(null);
      return;
    }
    const body = (await response.json()) as {
      configured: boolean;
      connected: { email: string | null } | null;
    };
    setGoogle({ configured: body.configured, connected: body.connected });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, []);

  useEffect(() => {
    void loadGoogle();
  }, [loadGoogle]);

  const googleToast = (title: MessageKey, variant: Toast['variant']): void => {
    setToasts((current) => [
      ...current,
      { id: `google-${String(Date.now())}`, title: t(title), variant },
    ]);
  };

  /* Back from a connect (`oauth-callback-screen.tsx`): say so once, and take the flag away. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') !== 'linked') return;
    params.delete('google');
    const rest = params.toString();
    window.history.replaceState(
      null,
      '',
      rest === '' ? window.location.pathname : `${window.location.pathname}?${rest}`,
    );
    googleToast('account.google.linked', 'success');
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival. */
  }, []);

  const googleHandlers = {
    onConnect: (): void => {
      setGoogleBusy(true);
      void (async () => {
        const challenge = await beginPkce();
        const response = await session
          .request(`/auth/google/link?code_challenge=${encodeURIComponent(challenge)}`)
          .catch(() => null);
        if (response?.ok !== true) {
          setGoogleBusy(false);
          googleToast('account.google.failed', 'error');
          return;
        }
        const body = (await response.json()) as { url: string };
        /* The callback comes back here, to this pane — `pkce.ts`'s `beginLink`. */
        beginLink(`${window.location.pathname}?section=integrations`);
        window.location.assign(body.url);
      })();
    },
    onDisconnect: (): void => {
      setGoogleBusy(true);
      void (async () => {
        const response = await session
          .request('/auth/google', { method: 'DELETE' })
          .catch(() => null);
        setGoogleBusy(false);
        if (response?.ok === true) {
          googleToast('account.google.unlinked', 'success');
        } else {
          const body = (await response?.json().catch(() => null)) as {
            error?: { code?: string };
          } | null;
          googleToast(
            body?.error?.code === 'identity_last' ? 'account.google.last' : 'account.google.failed',
            'error',
          );
        }
        await loadGoogle();
      })();
    },
  };

  /**
   * C54 · the caller's own skills, and the vocabulary to pick from.
   *
   * ⚠️⚠️ **`/skills/members/me` — THE CLIENT NEVER SENDS ITS OWN MEMBER ID.** The server
   * resolves `me` from the session, so *"my skills"* cannot be pointed at somebody else by
   * editing a request. See `subjectOf` in the controller.
   *
   * ⚠️ **Three reads, and a refusal on any of them is `[]` rather than a screen failure** —
   * the same argument the connections and applications reads above make: this is one tab of
   * several, and a read that did not land must not take the rest of the account with it.
   *
   * ⚠️ **Workspace-scoped, unlike the applications read.** A skill belongs to a MEMBER, and a
   * member is a person in one workspace, so this re-runs when the switcher moves.
   */
  const loadSkills = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setMySkills([]);
      setSkillLevels([]);
      setSkillVocabulary([]);
      return;
    }
    const [mine, levels, vocabulary] = await Promise.all([
      session.request('/skills/members/me').catch(() => null),
      session.request('/skills/levels').catch(() => null),
      session.request('/skills').catch(() => null),
    ]);
    setMySkills(mine?.ok === true ? (parseList(memberSkillSchema, await mine.json()) ?? []) : []);
    setSkillLevels(
      levels?.ok === true ? (parseList(skillLevelSchema, await levels.json()) ?? []) : [],
    );
    setSkillVocabulary(
      vocabulary?.ok === true ? (parseList(skillSchema, await vocabulary.json()) ?? []) : [],
    );
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, [session.workspaceId]);

  const skillsLoading = useFirstLoad(loadSkills);

  /**
   * ⚠️ **One writer for add, change-the-level and remove**, because the server has one row for
   * all three: `PUT` upserts it and `DELETE` takes it away.
   */
  const writeSkill = async (path: string, init: RequestInit): Promise<void> => {
    setSkillError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) {
      setSkillError(t('error.unavailable'));
      return;
    }
    await loadSkills();
  };

  /**
   * The return from Atlassian — `takeConnectReturn` reads it and takes it out of the address bar.
   *
   * ⚠️ 26 Oct 2026 · **a page now, not a toast.** The person was just on somebody else's site; a
   * line that vanished in four seconds was the whole of what met them back here, and a wrong site
   * picked at Atlassian needs more than one line to be fixable. The pane shows the result until
   * they leave it (`connect-result.tsx`).
   *
   * ⚠️ Set only when there was something to read: React runs this twice in development, and the
   * second run finds the query already gone — it must not clear what the first one found.
   */
  const [connectResult, setConnectResult] = useState<ConnectReturnRead | null>(null);
  useEffect(() => {
    const returned = takeConnectReturn();
    if (returned === null) return;
    setConnectResult(returned);
    if (returned.outcome === 'ok') void loadConnections();
  }, [loadConnections]);

  /** Every mutation re-reads, so the version travels forward and a stale write cannot follow. */
  const write = useCallback(
    async (path: string, body: unknown, method = 'PUT'): Promise<void> => {
      await session.request(path, { method, body: JSON.stringify(body) });
      await reload();
    },
    [reload, session],
  );

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <AccountScreen
        section={section}
        onSectionChange={(next) => {
          setParams({ section: next });
        }}
        connections={connections}
        connectResult={connectResult}
        onConnectResultDone={() => {
          setConnectResult(null);
          setParams({ section: 'integrations' });
        }}
        busy={connecting}
        onConnect={(instanceId) => {
          setConnecting(true);
          void (async () => {
            const response = await session
              .request(`/integrations/${instanceId}/oauth/start`, {
                method: 'POST',
                /* ⚠️ Where the callback sends the browser back to. A path — the server refuses
                   anything absolute, because this ends in a redirect. */
                /* ⚠️ The SECTION, not just the screen — landing on Profile after connecting
                   leaves somebody one click from the answer to "did that work?". */
                body: JSON.stringify({ returnTo: '/account?section=integrations' }),
              })
              .catch(() => null);
            if (response?.ok !== true) {
              setConnecting(false);
              setToasts((current) => [
                ...current,
                {
                  id: `connect-${String(Date.now())}`,
                  title: t('account.connections.result.failed'),
                  variant: 'error',
                },
              ]);
              return;
            }
            const body = (await response.json()) as { data?: { url?: unknown } };
            const url = body.data?.url;
            /**
             * ⚠️⚠️ **A FULL NAVIGATION, NOT A FETCH.** The consent screen is Atlassian's page and
             * has to be seen: following the redirect inside an XHR would render somebody's login
             * form into a response body nobody looks at, and the flow would simply never complete.
             */
            if (typeof url === 'string') window.location.assign(url);
            else setConnecting(false);
          })();
        }}
        mySkills={mySkills}
        skillsLoading={skillsLoading}
        skillLevels={skillLevels}
        skillVocabulary={skillVocabulary}
        skillError={skillError}
        onSetMySkill={(skillId, levelId) => {
          void writeSkill('/skills/members/me', {
            method: 'PUT',
            body: JSON.stringify({ skillId, levelId }),
          });
        }}
        onRemoveMySkill={(skillId) => {
          void writeSkill(`/skills/members/me/${encodeURIComponent(skillId)}`, {
            method: 'DELETE',
          });
        }}
        onDisconnect={(remoteSiteId) => {
          void (async () => {
            const response = await session
              .request(`/integrations/connections/${encodeURIComponent(remoteSiteId)}`, {
                method: 'DELETE',
              })
              .catch(() => null);
            if (response?.ok !== true) return;
            {
              const list = parseList(trackerConnectionSchema, await response.json()) ?? [];
              setConnections(list);
              trackerConnection.update(list);
            }
          })();
        }}
        account={account}
        onSaveProfile={(patch) => write('/account/profile', patch)}
        onReload={reload}
        onSavePreferences={(patch) => write('/account/preferences', patch)}
        onSaveNotifications={(patch) => write('/account/notifications', patch)}
        onChangeEmail={(email) => write('/auth/email', { email }, 'POST')}
        google={google}
        googleHandlers={googleHandlers}
        googleBusy={googleBusy}
        applications={applications}
        applicationHandlers={{
          onDisconnect: async (clientId, workspaceId) => {
            /* ⚠️ The tokens die with the consent, in the database (`0124`'s trigger) — so this is
             * not bookkeeping: the application stops working on its next request. The list is
             * re-read rather than filtered locally, because the server is what decided. */
            await session.request(OAUTH_PATHS.connection(clientId, workspaceId), {
              method: 'DELETE',
            });
            await loadApplications();
          },
        }}
        tokens={{
          onCreate: async ({ name, scopes }) => {
            /* ⚠️ C51.1 · the workspace is added HERE, because this is the layer that knows which
             * one is active — and the server refuses a body naming any other (R-39). A section
             * component that reached for it would be a screen defining its own context. */
            const response = await session.request('/account/tokens', {
              method: 'POST',
              body: JSON.stringify({ name, workspaceId: session.workspaceId, scopes }),
            });
            /* Parsed with the shared schema, like every other response — and `createdTokenSchema` is
             * the one shape that carries a token value, so getting it wrong would mean showing an
             * empty reveal dialog for a credential that cannot be recovered. */
            const parsed = createdTokenSchema.safeParse(await response.json());
            if (!parsed.success) throw new Error('the API returned an unexpected token shape');
            const created = parsed.data;
            await reload();
            return created;
          },
          onRevoke: async (id) => {
            await session.request(`/account/tokens/${id}`, { method: 'DELETE' });
            await reload();
          },
        }}
        security={{
          loading,
          passkeys,
          sessions,
          onChangePassword: async (currentPassword, newPassword) => {
            await session.request('/auth/password', {
              method: 'POST',
              body: JSON.stringify({
                current_password: currentPassword,
                new_password: newPassword,
              }),
            });
          },

          /**
           * ⚠️ **Two round trips with a browser ceremony between them**, and the middle step is the
           * one that can be dismissed. A person who closes the prompt has not failed at anything, so
           * `isCancellation` swallows it — an error message for somebody who changed their mind
           * trains them to ignore error messages.
           */
          onAddPasskey: async (name, hint) => {
            /* ⚠️ The name on THIS request is only what the browser shows in its own prompt — the
               stored one is decided by the second call, after the AAGUID exists. `hint` stands in
               when the person typed nothing, so the prompt never says "Passkey". */
            const begin = await session.request('/auth/passkeys/register/options', {
              method: 'POST',
              body: JSON.stringify({ name: name === '' ? hint : name }),
            });
            if (!begin.ok) return;

            try {
              const attestation = await createPasskey(
                (await begin.json()) as Parameters<typeof createPasskey>[0],
              );
              /* ⚠️ **An ABSENT name means *you name it*** — item 17. The server reads the
                 authenticator out of the attestation it just verified and falls back to the hint
                 for an AAGUID its table does not carry. */
              await session.request('/auth/passkeys/register', {
                method: 'POST',
                body: JSON.stringify({
                  ...(name === '' ? {} : { name }),
                  hint,
                  response: attestation,
                }),
              });
            } catch (error) {
              if (!isCancellation(error)) throw error;
              /* Dismissed. The challenge expires on its own; nothing to clean up. */
              return;
            }

            await reload();
          },

          onRemovePasskey: async (id) => {
            await session.request(`/auth/passkeys/${id}`, { method: 'DELETE' });
            await reload();
          },

          onRevokeSession: async (id) => {
            await session.request(`/auth/sessions/${id}`, { method: 'DELETE' });
            await reload();
          },

          onRevokeOthers: async () => {
            await session.request('/auth/sessions/revoke-others', { method: 'POST' });
            await reload();
          },

          twoFactorEnabled: twoFactor?.enabled ?? false,
          recoveryCodesLeft: twoFactor?.recoveryCodesLeft ?? 0,

          onEnrolTotp: async () => {
            const response = await session.request('/auth/totp', {
              method: 'POST',
              body: JSON.stringify({ friendly_name: 'Authenticator' }),
            });
            const body = (await response.json()) as {
              factor_id: string;
              secret: string;
              qr_code: string;
            };
            /* The factor id is carried in the closure rather than in component state: it is
             * meaningful only between enrolling and verifying, and state would keep it alive after
             * the dialog closes. */
            enrolledFactor.current = body.factor_id;
            /* ⚠️⚠️ **`qr_code` HAS BEEN IN THIS RESPONSE SINCE C6 AND NOTHING RENDERED IT** — the
               card asked people to type a 32-character base32 secret into a phone by hand. Item 11
               is where the picture the server was already drawing reaches the screen. */
            return { secret: body.secret, qrCode: body.qr_code };
          },

          onVerifyTotp: async (code) => {
            const factorId = enrolledFactor.current;
            if (factorId === null) throw new Error('nothing enrolled');
            const response = await session.request('/auth/totp/verify', {
              method: 'POST',
              body: JSON.stringify({ factor_id: factorId, code }),
            });
            /* ⚠️ A refusal has to REJECT, because the dialog distinguishes a wrong code from a
               successful enrolment by exactly this — `session.request` resolves for a 400 as
               readily as for a 201. */
            if (!response.ok) throw new Error('code refused');

            const body = (await response.json()) as { recovery_codes: readonly string[] };
            enrolledFactor.current = null;
            await readTwoFactor();
            return body.recovery_codes;
          },

          onDisableTotp: async (password) => {
            const factorId = twoFactor?.factorId;
            if (factorId == null) throw new Error('nothing to remove');
            const response = await session.request('/auth/totp/remove', {
              method: 'POST',
              body: JSON.stringify({ factor_id: factorId, password }),
            });
            if (!response.ok) throw new Error('refused');
            await readTwoFactor();
          },

          onRegenerateRecoveryCodes: async (password) => {
            const response = await session.request('/auth/totp/recovery', {
              method: 'POST',
              body: JSON.stringify({ password }),
            });
            if (!response.ok) throw new Error('refused');
            const body = (await response.json()) as { recovery_codes: readonly string[] };
            await readTwoFactor();
            return body.recovery_codes;
          },
        }}
        onPreviewTheme={() => undefined}
      />
      <Toaster
        toasts={[...toasts]}
        label={t('settings.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((one) => one.id !== id));
        }}
      />
    </ScreenState>
  );
}
